<div class="page">
    <div class="page-head">
        <div>
            <div style="display: flex; justify-content: space-between; align-items: center;">
                <h1 class="page-title">S3 Bucket Management</h1>
                <nz-space>
                    <button nz-button (click)="syncBuckets()"><nz-icon nzType="sync" /> Sync</button>
                    <button nz-button nzType="primary" (click)="isFormOpen.set(true)">
                        <nz-icon nzType="plus" />
                        Add Bucket</button>
                </nz-space>
            </div>
            <p class="page-subtitle">
                Register new S3 buckets, view connected storage, and launch the file explorer to browse bucket contents.
            </p>
        </div>
    </div>

    <nz-divider class="rule" />

    <nz-radio-group [(ngModel)]="selectedBucket" style="width: 100%;">
        <nz-table [nzData]="buckets()" [nzLoading]="this.bucketsResource.isLoading()" [nzNoResult]="emptyData"
            #bucketsTable nzShowSizeChanger [nzShowTotal]="totalTemplate" nzPaginationType="small">
            <thead>
                <tr>
                    <th nzWidth="5px"></th>
                    <!-- $any() below is only to remove the sytanx highlighting... -->
                    @for (col of columns; track col.name) {
                    <th [nzWidth]="col.width ?? null" [nzRight]="col.right ?? false" [nzSortFn]="$any(col.sortFn)"
                        [nzFilters]="$any(col.filters)" [nzFilterFn]="$any(col.filterFn)"
                        [nzFilterMultiple]="col.filterMultiple ?? true" [nzCustomFilter]="col.searchable">
                        {{ col.name }}

                        @if (col.searchable) {
                        <ng-container ngProjectAs="nz-filter-trigger">
                            <nz-filter-trigger [(nzVisible)]="col.visible!"
                                [nzActive]="(col.searchValue ?? '').length > 0" [nzDropdownMenu]="menu">
                                <nz-icon nzType="search" />
                            </nz-filter-trigger>
                            <nz-dropdown-menu #menu="nzDropdownMenu">
                                <div class="ant-table-filter-dropdown">
                                    <form (ngSubmit)="search(col)" style="padding: 5px 12px; ">
                                        <input nzSize="small" type="text" nz-input [placeholder]="'Search ' + col.name"
                                            [name]="col.name" [(ngModel)]="col.searchValue" />
                                        <div style="margin-top: 10px;">
                                            <div style="display: flex; justify-content: space-between; width: 100%;">
                                                <button nzType="link" [disabled]="col.searchValue?.length == 0"
                                                    nzSize="small" nz-button (click)="reset(col)" type="reset">
                                                    Reset
                                                </button>
                                                <button nzSize="small" nz-button nzType="primary" type="submit">
                                                    OK
                                                </button>
                                            </div>
                                        </div>

                                        <!-- </ul> -->
                                    </form>
                                </div>
                            </nz-dropdown-menu>
                        </ng-container>
                        }
                    </th>
                    }
                    <th nzWidth="140px" nzRight>Action</th>
                </tr>
            </thead>
            <tbody>
                @for (data of bucketsTable.data; track data.id) {
                <tr (click)="selectedBucket.set(data)">
                    <td><label nz-radio [nzValue]="data"></label></td>
                    <td>{{data.name}}</td>
                    <td>{{data.alias}}</td>
                    <td><nz-tag>{{data.region}}</nz-tag></td>
                    <td>{{data.description}}</td>
                    <td>
                        <nz-space (click)="$event.stopPropagation()">
                            <a (click)="$event.stopImmediatePropagation(); editBucket(data)"> <nz-icon
                                    nzType="edit" /></a>
                            <a nz-popconfirm nzOkText="Deregister" nzOkType="danger"
                                [nzPopconfirmTitle]="'Deregister ' + data.alias + '? The S3 bucket itself is not deleted.'"
                                (nzOnConfirm)="deleteBucket(data)">
                                <nz-icon nzType="delete" />
                            </a>
                            <a routerLink="/aws-utilities/s3-explorer" [queryParams]="{bucket: data.name}"
                                title="Browse in the S3 explorer">
                                <nz-icon nzType="delivered-procedure" nzTheme="outline" />
                            </a>
                        </nz-space>
                    </td>
                </tr>
                }
            </tbody>
        </nz-table>
        <ng-template #totalTemplate let-total let-range="range">
            {{ range[0] }}-{{ range[1] }} of {{ total }} items
        </ng-template>
    </nz-radio-group>

    <ng-template #emptyData>
        @if (bucketsResource.error(); as err) {
        <nz-empty nzNotFoundImage="simple" [nzNotFoundContent]="err.message ?? 'Could not load registered buckets.'"
            [nzNotFoundFooter]="retry" />
        <ng-template #retry>
            <button nz-button (click)="bucketsResource.reload()">
                <nz-icon nzType="reload" />
                Retry
            </button>
        </ng-template>
        } @else {
        <nz-empty nzNotFoundImage="simple" nzNotFoundContent="No buckets registered yet." />
        }
    </ng-template>
</div>

<nz-drawer [nzVisible]="!!selectedBucket()" [nzTitle]="'Bucket: ' + selectedBucket()?.name"
    (nzOnClose)="selectedBucket.set(null)" [nzWidth]="720">
    <ng-container *nzDrawerContent>
        @if (selectedBucket(); as bucket){
        <h2 class="page-title"> Lifecycle configuration for bucket: {{bucket.name}}</h2>
        <p class="page-subtitle">
            Automated rules to transition or expire objects over time, reducing storage costs. Templates for usual
            configurations provided below.
        </p>

        <nz-divider class="rule" />

        @if (bucketLifecycleResource.isLoading()){
        <nz-spin nzSpinning />
        }
        @else if (bucketLifecycleResource.error(); as error) {
        <nz-alert nzType="error" [nzMessage]="error.message" />
        }
        @else if(bucketLifecycleResource.hasValue())
        {
        @let lifecycle = bucketLifecycleResource.value();

        <!-- <div style="display: flex; gap: 10px"> -->
        <div>
            <div style="flex: 1">
                <app-bucket-lifecycle-json-editor [configuration]="lifecycle.lifecycle_configuration"
                    [saving]="isSaving()" (save)="saveLifecycleConfiguration($event)" />
            </div>
            <div style="flex: 1">
                <h3 class="page-title"> Lifecycle configuration templates</h3>
                <p class="page-subtitle">
                    Templates for lifecycle configuration rules
                </p>
                <nz-divider class="rule" />
                <nz-collapse [nzBordered]="false">
                    @for (tpl of templates; track tpl.key) {
                    <nz-collapse-panel [nzHeader]="tpl.name" [nzExtra]="extra">
                        <p class="hint">{{ tpl.hint }}</p>
                        <pre class="json-preview">{{ tpl.rules | json }}</pre>
                        <!-- per-panel extra: a quick copy icon in the header itself -->
                        <ng-template #extra>
                            <nz-icon nzType="copy" class="header-copy" nz-tooltip nzTooltipTitle="Copy JSON"
                                (click)="copy(tpl)" />
                        </ng-template>
                    </nz-collapse-panel>
                    }
                </nz-collapse>
            </div>
        </div>
        }
        }
    </ng-container>

</nz-drawer>

<nz-modal [(nzVisible)]="isFormOpen" nzTitle="Add Bucket" (nzOnCancel)="isFormOpen.set(false); edittingBucket.set(null)"
    (nzOnOk)="bucketForm()?.submitForm(); isFormOpen.set(false)">
    <ng-container *nzModalContent>
        <app-bucket-form #addBucketForm (saved)="bucketsResource.reload()" [(activeBucket)]="edittingBucket"
            [hideButtons]="true" />
    </ng-container>
</nz-modal>